import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowUpRight } from 'lucide-react';
import { NAV_ITEMS } from '../mock';
import { Button } from './ui/button';

const Logo = () => (
  <Link to="/" className="flex items-center">
    <img src="/logo.png" alt="CoreDesk" width="148" height="36" className="h-9 w-auto" />
  </Link>
);

const DesktopMenu = () => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <nav className="hidden lg:flex items-center gap-1">
      {NAV_ITEMS.map((item, idx) => {
        const hasChildren = !!item.children;
        return (
          <div
            key={item.label}
            className="relative"
            onMouseEnter={() => hasChildren && setOpenIdx(idx)}
            onMouseLeave={() => setOpenIdx(null)}
          >
            {hasChildren ? (
              <button className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-[#0a0e27] flex items-center gap-1 rounded-md hover:bg-slate-100 transition-colors">
                {item.label}
                <ChevronDown size={14} className={`transition-transform ${openIdx === idx ? 'rotate-180' : ''}`} />
              </button>
            ) : (
              <Link to={item.to} className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-[#0a0e27] rounded-md hover:bg-slate-100 transition-colors block">
                {item.label}
              </Link>
            )}
            {hasChildren && openIdx === idx && (
              <div className="absolute top-full left-0 pt-3 w-[420px] z-50">
                <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 p-3">
                  {item.children.map((c) => (
                    <Link
                      key={c.label}
                      to={c.to}
                      className="flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 transition group"
                    >
                      <div className="mt-0.5 w-9 h-9 rounded-lg bg-[#FFF1EA] flex items-center justify-center text-[#FF5B24] font-bold group-hover:bg-[#FF5B24] group-hover:text-white transition-colors">
                        {c.label.charAt(0)}
                      </div>
                      <div>
                        <div className="font-semibold text-[#0a0e27] text-sm">{c.label}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{c.desc}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    if (location.hash) {
      const el = document.getElementById(location.hash.replace('#', ''));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [location]);

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-md border-b border-slate-100 shadow-sm' : 'bg-white/70 backdrop-blur-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <Logo />
          <DesktopMenu />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/entrar')} className="hidden md:inline-block px-3 py-2 text-sm font-medium text-slate-700 hover:text-[#0a0e27]">
            Entrar
          </button>
          <Button onClick={() => navigate('/contato')} className="btn-coral rounded-full h-10 px-5 text-sm font-semibold gap-1.5">
            Fale conosco
            <ArrowUpRight size={16} />
          </Button>
          <button className="lg:hidden ml-1 p-2 rounded-md hover:bg-slate-100" onClick={() => setMobileOpen((v) => !v)} aria-label="Menu">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-100 bg-white">
          <div className="px-4 py-3 space-y-1">
            {NAV_ITEMS.map((item) => (
              <Link key={item.label} to={item.to || '/'} className="block py-2 text-sm font-medium text-slate-700">
                {item.label}
              </Link>
            ))}
            <Link to="/entrar" className="block py-2 text-sm font-medium text-slate-700">Entrar</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
